import {
  ActionIcon,
  Box,
  Flex,
  Indicator,
  Paper,
  Stack,
  Text,
} from "@mantine/core";
import { IconBell, IconChevronRight } from "@tabler/icons-react";
import type { SessionWithTrainerDisplayListOutput } from "@/api-types";
import { FancyInlineLoader, FancyLoader } from "@/components/FancyLoader";
import { Portrait } from "@/components/Portrait";
import { SessionCard } from "@/components/SessionCard";
import { TitleWithLink } from "@/components/TitleWithLink";
import { useUnreadNotificationsCount } from "@/hooks/useNotifications";
import { useOwnerProfileDisplay } from "@/hooks/useProfileDisplay";
import { useSessionsByStatus } from "@/hooks/useSessionsByStatus";
import { OwnerRoutes, UniversalRoutes, useNavigator } from "@/routers/routes";

const SessionsList = ({
  sessions,
  title,
  showNothingIfEmpty = false,
}: {
  sessions: SessionWithTrainerDisplayListOutput[];
  title: string;
  showNothingIfEmpty?: boolean;
}) => {
  const { navigator } = useNavigator();

  if (sessions.length === 0 && showNothingIfEmpty) {
    return null;
  }

  const content =
    sessions.length === 0 ? (
      <Paper
        p="md"
        mb="sm"
        bg="blue.0"
        onClick={navigator({ path: OwnerRoutes.LIST_TRAINERS })}
      >
        <Flex gap="sm" align="center">
          <Box style={{ flex: "1 1 auto" }}>
            <Text>Click to find a trainer!</Text>
          </Box>
          <Box>
            <ActionIcon variant="transparent" size="lg" color="gray">
              <IconChevronRight />
            </ActionIcon>
          </Box>
        </Flex>
      </Paper>
    ) : (
      <Stack>
        {sessions.map((session) => (
          <SessionCard key={session.id} session={session} who="owner" />
        ))}
      </Stack>
    );

  return (
    <>
      <TitleWithLink title={title} href={UniversalRoutes.SESSIONS.LIST} />
      {content}
    </>
  );
};

export function Home() {
  const { navigator } = useNavigator();
  const { profile, isLoading: isProfileLoading } = useOwnerProfileDisplay();
  const { unreadCount } = useUnreadNotificationsCount();
  const { sessionsByStatus, isLoading } = useSessionsByStatus("owner", [
    "REQUESTED",
    "CONFIRMED",
    "COMPLETED",
  ]);

  if (isProfileLoading || !profile) {
    return <FancyLoader />;
  }

  const dog = profile.dogs[0];

  if (!dog) {
    return <FancyLoader />;
  }

  return (
    <Stack>
      <Flex gap="xs" align="center" justify="space-between">
        <Flex gap="xs" align="center">
          <Portrait profile={profile} size="lg" />
          <Portrait profile={dog} size="lg" who="dog" />
          <Stack gap="0">
            <Text fw="bold" size="xl">
              Welcome back
            </Text>
            <Text c="dimmed">
              {profile.firstName} and {dog.name}
            </Text>
          </Stack>
        </Flex>
        <Box>
          <Indicator
            size={20}
            offset={5}
            position="top-end"
            color="dark-accent.3"
            withBorder
            disabled={!unreadCount}
            label={
              <Text size="xs" fw={"bold"}>
                {unreadCount}
              </Text>
            }
          >
            <ActionIcon
              variant="transparent"
              size="lg"
              color="gray"
              onClick={navigator({
                path: UniversalRoutes.NOTIFICATIONS.LIST,
              })}
            >
              <IconBell />
            </ActionIcon>
          </Indicator>
        </Box>
      </Flex>

      {isLoading ? (
        <FancyInlineLoader />
      ) : (
        <SessionsList
          title="Upcoming sessions"
          sessions={[
            ...(sessionsByStatus.REQUESTED || []),
            ...(sessionsByStatus.CONFIRMED || []),
          ]}
        />
      )}

      {isLoading ? (
        <FancyInlineLoader />
      ) : (
        <SessionsList
          title="Past sessions"
          sessions={sessionsByStatus.COMPLETED || []}
          showNothingIfEmpty
        />
      )}
    </Stack>
  );
}
